import React, { useMemo, useState } from 'react'
import { ActionIcon, Box, Fieldset, Group, Menu, NumberInput, Select, Textarea, TextInput, Tooltip } from '@mantine/core'
import { IconAdjustmentsAlt, IconLibrary, IconTrash } from '@tabler/icons-react'
import { ReportParams } from './types'
import { useScopes } from '../../hooks/reports/use-scopes'
import { useSaveReport } from '../../hooks/reports/use-save-report'
import { CloneReportModal } from './Modals/CloneReportModal'
import { DeleteReportConfirmationModal } from './Modals/DeleteReportConfirmationModal'
import { deepEqual } from '../../lib/utils'

type ReportParametersProps = {
    parameters: ReportParams
    updateParams: (params: Partial<ReportParams>) => void
}

export const ReportParameters = ({ parameters, updateParams }: ReportParametersProps) => {
    const [cloneModalOpen, setCloneModalOpen] = useState(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const { report_pk, report_name, report_description, monetary_rounding, percent_rounding, numeric_rounding, salary_display_code, scope_pk, category_pk } = parameters
    const initialValues = useMemo(() => parameters, [])
    const { data: scopeList } = useScopes()

    const preventMenuClose = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    const scopes = scopeList?.map(s => ({ value: s.scope_pk.toString(), label: s.scope_name }))

    const monetaryValues = [
        { value: 'annual', label: 'Annual'},
        { value: 'hourly', label: 'Hourly'},
        { value: 'millions', label: 'Millions'},
        { value: 'monthly', label: 'Monthly'},
        { value: 'thousands', label: 'Thousands'},
    ]

    const getScope = (scopePk: number) => {
        return scopeList?.find(s => s.scope_pk === scopePk)
    }

    const { mutate: saveReport, isError, reset } = useSaveReport()

    if (isError) {
        reset()
        updateParams(initialValues)
    }

    return (
        <>
            <CloneReportModal opened={cloneModalOpen} onClose={() => setCloneModalOpen(false)} reportPk={report_pk} categoryPk={category_pk} scopeCode={getScope(scope_pk)?.scope_code || ''} />
            <DeleteReportConfirmationModal reportPk={report_pk} opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} />
            <Menu shadow="md" width={200} opened={menuOpen} onChange={setMenuOpen} onClose={() => !deepEqual(initialValues, parameters) ? saveReport(parameters) : undefined}>
                <Menu.Target>
                    <Tooltip label='Report Parameters'>
                        <ActionIcon variant='outline' size='xl'>
                            <IconAdjustmentsAlt style={{ width: '70%', height: '70%' }} stroke={1.5} />
                        </ActionIcon>
                    </Tooltip>
                </Menu.Target>

                <Menu.Dropdown w={600} mih={600} p='sm'>
                    <Group justify='flex-end'>
                        <Tooltip label='Clone Report'>
                            <ActionIcon variant='transparent' size='xl' onClick={() => {
                                setMenuOpen(false)
                                setCloneModalOpen(true)
                            }}>
                                <IconLibrary style={{ width: '80%', height: '80%' }} stroke={1.5} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label='Delete Report'>
                            <ActionIcon variant='transparent' color='red' size='xl' onClick={() => {
                                setMenuOpen(false)
                                setDeleteModalOpen(true)
                            }}>
                                <IconTrash style={{ width: '80%', height: '80%' }} stroke={1.5} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                    <TextInput label="Report name" placeholder="Report Name" value={report_name} onChange={(e) => updateParams({ report_name: e.target.value })
                    } />
                    <Textarea label="Report description" placeholder="Report description" value={(report_description || report_description === 'null') ? '' : report_description} onChange={(e) => updateParams({ report_description: e.target.value })
                    } />
                    <Box onMouseDown={preventMenuClose} onClick={preventMenuClose}>
                        <Select label="Publish to" data={scopes || []} value={scope_pk.toString()} onChange={(v) => updateParams({ scope_pk: Number(v), scope_name: getScope(Number(v))?.scope_name, scope_code: getScope(Number(v))?.scope_code })} />
                    </Box>
                    <Box onMouseDown={preventMenuClose} onClick={preventMenuClose}>
                        <Select label="Show monetary values as" data={monetaryValues} value={salary_display_code} onChange={(v) => updateParams({ salary_display_code: v?.toString() })} />
                    </Box>
                    <Fieldset legend="Value Rounding">
                        <Group flex={3} wrap='nowrap'>
                            <NumberInput w={150} label="Numeric" value={Number(numeric_rounding)} decimalScale={0} max={10} min={-10} onChange={(val) => updateParams({ numeric_rounding: val?.toString() })} />
                            <NumberInput w={150} label="Monetary" value={Number(monetary_rounding)} decimalScale={0} max={10} min={-10} onChange={(val) =>updateParams({ monetary_rounding: val?.toString() })} />
                            <NumberInput w={150} label="Percent" value={Number(percent_rounding)} decimalScale={0} max={10} min={-10} onChange={(val) => updateParams({ percent_rounding: val?.toString() })} />
                        </Group>
                    </Fieldset>
                </Menu.Dropdown>
            </Menu>
        </>
    )
}
